import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { 
  Delete as DeleteIcon, 
  DragIndicator as DragIcon,
  Edit as EditIcon 
} from '@mui/icons-material';
import { flows, questions } from '../../services/api';
import CreateQuestionDialog from '../../components/questions/CreateQuestionDialog';
import { QuestionEditor } from '../../components/questions/QuestionEditor';
import { Question, Flow, CreateFlowData, QuestionType } from '../../types';

interface FlowQuestion extends Question {
  order: number;
}

const FlowManagement: React.FC = () => {
  const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);
  const [availableFlows, setAvailableFlows] = useState<Flow[]>([]);
  const [flowQuestions, setFlowQuestions] = useState<FlowQuestion[]>([]);
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);
  const [addQuestionDialogOpen, setAddQuestionDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<number | ''>('');
  const [createFlowDialogOpen, setCreateFlowDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [questionToEdit, setQuestionToEdit] = useState<Question | null>(null);
  const [newFlow, setNewFlow] = useState<CreateFlowData>({
    name: '',
    title: '',
    description: '',
    frequency: 'daily'
  });
  const [addQuestionTab, setAddQuestionTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load flows on component mount
  useEffect(() => {
    loadFlows();
  }, []);

  // Load questions when a flow is selected
  useEffect(() => {
    if (selectedFlow) {
      loadFlowQuestions(selectedFlow.id);
    }
  }, [selectedFlow]);

  // Load questions when add question dialog opens
  useEffect(() => {
    if (addQuestionDialogOpen) {
      loadQuestions();
    }
  }, [addQuestionDialogOpen]);

  const loadFlows = async () => {
    try {
      const response = await flows.getAll();
      setAvailableFlows(response.data);
    } catch (err) {
      setError('Failed to load flows');
      console.error('Failed to load flows:', err);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await questions.getAll();
      setAvailableQuestions(response.data);
    } catch (err) {
      setError('Failed to load questions');
      console.error('Failed to load questions:', err);
    }
  };

  const loadFlowQuestions = async (flowId: number) => {
    try {
      // Find the flow to get its name
      const flow = availableFlows.find(f => f.id === flowId);
      if (!flow) {
        setError('Flow not found');
        return;
      }

      const response = await flows.getQuestions(flow.name);
      setFlowQuestions(response.data);
    } catch (err) {
      setError('Failed to load flow questions');
      console.error('Failed to load flow questions:', err);
    }
  };

  const refreshCurrentFlow = async () => {
    if (selectedFlow) {
      await loadFlowQuestions(selectedFlow.id);
    }
  };

  const handleAddQuestion = async () => {
    if (!selectedFlow || !selectedQuestion) return;

    try {
      setIsSubmitting(true);
      await flows.addQuestion(selectedFlow.id, {
        question_id: selectedQuestion,
        order: flowQuestions.length
      });
      await refreshCurrentFlow();
      setAddQuestionDialogOpen(false);
      setSelectedQuestion('');
    } catch (err) {
      setError('Failed to add question');
      console.error('Failed to add question:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAndAddQuestion = async (questionData: any) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const response = await questions.create(questionData);
      console.log('Question created:', response.data);

      if (selectedFlow) {
        await flows.addQuestion(selectedFlow.id, {
          question_id: response.data.id,
          order: flowQuestions.length
        });
        await refreshCurrentFlow();
      }

      setAddQuestionDialogOpen(false);
    } catch (error) {
      console.error('Failed to create question:', error);
      setError('Failed to create question');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReorderQuestions = async (newQuestions: FlowQuestion[]) => {
    if (!selectedFlow?.id) return;

    try {
      const questionIds = newQuestions.map(q => q.id).filter((id): id is number => id !== undefined);
      await flows.reorderQuestions(selectedFlow.id, questionIds);
      await refreshCurrentFlow();
    } catch (err) {
      console.error('Error reordering questions:', err);
      setError('Failed to reorder questions');
    }
  };

  const handleRemoveQuestion = async (questionId: number | undefined) => {
    if (!selectedFlow?.id || !questionId) return;

    try {
      await flows.removeQuestion(selectedFlow.id, questionId);
      await refreshCurrentFlow();
    } catch (err) {
      console.error('Error removing question:', err);
      setError('Failed to remove question');
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !selectedFlow) return;

    const newQuestions = Array.from(flowQuestions);
    const [removed] = newQuestions.splice(result.source.index, 1);
    newQuestions.splice(result.destination.index, 0, removed);

    await handleReorderQuestions(newQuestions);
  };

  const handleAddQuestionClick = () => {
    setAddQuestionDialogOpen(true);
    setAddQuestionTab(0);
  };

  const handleEditQuestion = async (updatedQuestion: Question) => {
    if (!updatedQuestion.id) return;

    setIsSubmitting(true);
    try {
      // Create a copy of the question to modify
      const questionToUpdate = { ...updatedQuestion };
      
      // If question_type_display exists but question_type doesn't, use question_type_display value for question_type
      if (questionToUpdate.question_type_display && !questionToUpdate.question_type) {
        questionToUpdate.question_type = questionToUpdate.question_type_display as QuestionType;
      }
      
      // Ensure question_type is set correctly
      if (!questionToUpdate.question_type) {
        throw new Error('Question type is required');
      }
      
      // Process options to ensure they have the correct format
      if (questionToUpdate.options && questionToUpdate.options.length > 0) {
        questionToUpdate.options = questionToUpdate.options.map((opt, index) => ({
          ...opt,
          // Ensure front_text is set
          front_text: opt.front_text || '',
          back_text: opt.back_text || '',
          order: index
        }));
      }
      
      // Preserve the key field
      questionToUpdate.key = updatedQuestion.key;
      
      // Ensure ID is defined before calling update
      if (typeof questionToUpdate.id === 'number') {
        await questions.update(questionToUpdate.id, questionToUpdate);
      } else {
        throw new Error('Question ID is required');
      }
      
      setEditDialogOpen(false);
      setQuestionToEdit(null);
      await refreshCurrentFlow();
    } catch (err) {
      setError('Failed to update question');
      console.error('Failed to update question:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (question: Question) => {
    setQuestionToEdit(question);
    setEditDialogOpen(true);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Flow Management
        </Typography>
        <Button
          variant="contained"
          onClick={() => setCreateFlowDialogOpen(true)}
          sx={{ mb: 2 }}
        >
          Create New Flow
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <List>
          {availableFlows.map((flow) => (
            <ListItemButton
              key={flow.id}
              onClick={() => setSelectedFlow(flow)}
              selected={selectedFlow?.id === flow.id}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemText primary={flow.name} secondary={flow.description} />
            </ListItemButton>
          ))}
        </List>

        {selectedFlow && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Questions in {selectedFlow.name}
            </Typography>
            <Button
              variant="contained"
              onClick={handleAddQuestionClick}
              sx={{ mb: 2 }}
            >
              Add Question
            </Button>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable-questions">
                {(provided) => (
                  <List {...provided.droppableProps} ref={provided.innerRef}>
                    {flowQuestions.map((question, index) => (
                      <Draggable
                        key={question.id}
                        draggableId={String(question.id)}
                        index={index}
                      >
                        {(provided) => (
                          <ListItemButton
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            divider
                          >
                            <Box {...provided.dragHandleProps} sx={{ mr: 2 }}>
                              <DragIcon />
                            </Box>
                            <ListItemText primary={question.text} />
                            <Box>
                              <IconButton 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditDialog(question);
                                }}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveQuestion(question.id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </ListItemButton>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        )}
      </Box>

      <Dialog
        open={addQuestionDialogOpen && addQuestionTab === 0}
        onClose={() => setAddQuestionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#121212',  
            boxShadow: 24,
            '& .MuiDialogTitle-root, & .MuiDialogContent-root': {
              color: '#fff'
            },
            '& .MuiInputBase-root': {
              bgcolor: '#1e1e1e',  
              color: '#fff'
            },
            '& .MuiInputLabel-root': {
              color: '#fff'
            },
            '& .MuiSelect-icon': {
              color: '#fff'
            },
            '& .MuiMenuItem-root': {
              bgcolor: '#1e1e1e',
              color: '#fff',
              '&:hover': {
                bgcolor: '#2d2d2d'
              }
            },
            '& .MuiMenu-paper': {
              bgcolor: '#1e1e1e',
              '& .MuiList-root': {
                bgcolor: '#1e1e1e',
                padding: 0
              }
            },
            '& .MuiButton-root': {
              color: '#fff'
            },
            '& .MuiPopover-paper': {
              bgcolor: '#1e1e1e'
            }
          }
        }}
      >
        <DialogTitle>Add Question to Flow</DialogTitle>
        <DialogContent>
          <Tabs value={addQuestionTab} onChange={(_, newValue) => setAddQuestionTab(newValue)}>
            <Tab label="Existing Question" />
            <Tab label="New Question" />
          </Tabs>

          {addQuestionTab === 0 && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Question</InputLabel>
                <Select
                  value={selectedQuestion}
                  onChange={(e) => setSelectedQuestion(e.target.value as number)}
                >
                  {availableQuestions
                    .filter(q => !flowQuestions.some(sq => sq.id === q.id))
                    .map((question) => (
                      <MenuItem key={question.id} value={question.id}>
                        {question.text}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddQuestionDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={addQuestionTab === 0 ? handleAddQuestion : handleCreateAndAddQuestion} 
            variant="contained"
          >
            {addQuestionTab === 0 ? 'Add Existing' : 'Create & Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {addQuestionDialogOpen && addQuestionTab === 1 && (
        <CreateQuestionDialog
          open={addQuestionDialogOpen}
          onClose={() => setAddQuestionDialogOpen(false)}
          onSubmit={handleCreateAndAddQuestion}
          isSubmitting={isSubmitting}
          error={error}
          inFlow={true}
          availableQuestions={availableQuestions}
          PaperProps={{
            sx: {
              bgcolor: '#121212',
              boxShadow: 24,
              '& .MuiDialogTitle-root, & .MuiDialogContent-root': {
                color: '#fff'
              },
              '& .MuiInputBase-root': {
                bgcolor: '#1e1e1e',
                color: '#fff'
              },
              '& .MuiInputLabel-root': {
                color: '#fff'
              },
              '& .MuiSelect-icon': {
                color: '#fff'
              },
              '& .MuiMenuItem-root': {
                bgcolor: '#1e1e1e',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#2e2e2e'
                }
              },
              '& .MuiMenu-paper': {
                bgcolor: '#1e1e1e',
                '& .MuiList-root': {
                  bgcolor: '#1e1e1e',
                  padding: 0
                }
              },
              '& .MuiButton-root': {
                color: '#fff'
              },
              '& .MuiPopover-paper': {
                bgcolor: '#1e1e1e'
              }
            }
          }}
        />
      )}
      {/* Edit Question Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#121212',
            boxShadow: 24,
            '& .MuiDialogTitle-root, & .MuiDialogContent-root': {
              color: '#fff'
            },
            '& .MuiInputBase-root': {
              bgcolor: '#1e1e1e',
              color: '#fff'
            },
            '& .MuiInputLabel-root': {
              color: '#fff'
            },
            '& .MuiSelect-icon': {
              color: '#fff'
            },
            '& .MuiMenuItem-root': {
              bgcolor: '#1e1e1e',
              color: '#fff',
              '&:hover': {
                bgcolor: '#2d2d2d'
              }
            },
            '& .MuiMenu-paper': {
              bgcolor: '#1e1e1e',
              '& .MuiList-root': {
                bgcolor: '#1e1e1e',
                padding: 0
              }
            },
            '& .MuiButton-root': {
              color: '#fff'
            },
            '& .MuiPopover-paper': {
              bgcolor: '#1e1e1e'
            }
          }
        }}
      >
        <DialogTitle>Edit Question</DialogTitle>
        <DialogContent>
          {questionToEdit && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <QuestionEditor
                question={{
                  text: questionToEdit.text,
                  category: questionToEdit.category,
                  question_type: (questionToEdit.question_type || questionToEdit.question_type_display) as QuestionType,
                  low_text: questionToEdit.low_text || '',
                  high_text: questionToEdit.high_text || '',
                  key: questionToEdit.key || '',
                  options: (questionToEdit.options || []).map(opt => ({
                    front_text: opt.front_text || '',
                    back_text: opt.back_text || '',
                    value: opt.value || 0,
                    is_other: opt.is_other || false
                  })),
                  video_url: questionToEdit.video_url,
                  visual_component_url: questionToEdit.visual_component_url
                }}
                onChange={(updatedQuestion) => {
                  // Convert the editor's Option type back to QuestionOption type
                  const updatedOptions = updatedQuestion.options.map((opt, index) => ({
                    id: questionToEdit.options?.[index]?.id || 0,
                    front_text: opt.front_text || '',
                    back_text: opt.back_text || '',
                    value: opt.value || 0,
                    is_other: opt.is_other || false,
                    order: index
                  }));

                  setQuestionToEdit({
                    ...questionToEdit,
                    ...updatedQuestion,
                    key: updatedQuestion.key,
                    options: updatedOptions
                  });
                }}
                showCategory={true}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => questionToEdit && handleEditQuestion(questionToEdit)}
            disabled={isSubmitting}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FlowManagement;